/**
 * Text Component
 *
 * Renders a string with an MSDF font asset (see the `font` asset type — produced by msdf-atlas-gen).
 * Mirrors SpriteRenderer's data-only "cold" style: methods just mutate @field data, which the text
 * render path re-reads each frame. Drawn by EditorViewRenderer.renderText (and the player's text path).
 */

import {
  component,
  displayName,
  icon,
  color,
  category,
  requireComponent,
  executeInEditor,
  allowMultiple,
  field,
} from "../attributes";

/** Horizontal alignment of the text block relative to the entity origin. */
export enum TextAlign {
  Left = 0,
  Center = 1,
  Right = 2,
}

@component("core.Text")
@displayName("Text")
@icon("Type")
@color("#4ecdc4")
@category("Rendering")
@requireComponent(["core.Transform"])
@executeInEditor(true)
@allowMultiple(false)
export class Text {
  @field({ type: "string", label: "Text", widget: "text", tooltip: "The string to render" })
  text: string = "Text";

  @field({ type: "asset", assetType: "font", label: "Font", tooltip: "MSDF font asset" })
  font: string = "";

  @field({ type: "f32", label: "Font Size", min: 1, tooltip: "Cap height in world units", group: "Layout" })
  fontSize: number = 32;

  @field({ type: "f32", label: "Line Height", min: 0.1, step: 0.05, tooltip: "Line spacing multiplier", group: "Layout" })
  lineHeight: number = 1.0;

  @field({
    type: "i32",
    label: "Align",
    widget: "dropdown",
    group: "Layout",
    options: [
      { label: "Left", value: TextAlign.Left },
      { label: "Center", value: TextAlign.Center },
      { label: "Right", value: TextAlign.Right },
    ],
  })
  align: number = TextAlign.Left;

  @field({ type: "color", label: "Color", tooltip: "Text tint" })
  color: { r: number; g: number; b: number; a: number } = { r: 255, g: 255, b: 255, a: 255 };

  @field({ type: "i32", label: "Sorting Layer" })
  sortingLayer: number = 1;

  @field({ type: "i32", label: "Order in Layer" })
  orderInLayer: number = 0;

  @field({ type: "bool", label: "Visible" })
  visible: boolean = true;

  // ── Runtime API (cold, like SpriteRenderer) ──────────────────────────────

  /** Replace the rendered string. */
  setText(text: string): void {
    this.text = text;
  }

  /** Set the tint color (0-255 per channel; alpha preserved if omitted). */
  setColor(r: number, g: number, b: number, a: number = this.color.a): void {
    this.color = { r, g, b, a };
  }

  /** Set the font asset id (empty clears it). */
  setFont(assetId: string): void {
    this.font = assetId;
  }

  /** Toggle render visibility. */
  setVisible(visible: boolean): void {
    this.visible = visible;
  }
}
